import clsx from 'clsx';
import React from 'react';

import { LoadingPanel } from '../Loading';
import { Layout } from './Layout';

interface PageTabsProps extends React.HTMLAttributes<HTMLElement> {
    orientation?: React.AriaAttributes['aria-orientation'];
    className?: string;
    children: React.ReactNode;
}

export function PageTabs(props: PageTabsProps) {
    const { orientation = 'horizontal', children, className, ...rest } = props;

    // Usage: `vertical` tabs must be passed to the `nav` prop of `PageSection`.
    if (orientation === 'vertical') {
        return (
            <div {...rest} className={clsx('flex min-w-[11.25rem] flex-col gap-3', className)}>
                {children}
            </div>
        );
    }

    return (
        <Layout kind="centered" className={clsx('border-b', props.className)}>
            {props.children}
        </Layout>
    );
}

/**
 * The page intro of a page with tabs
 */
export function PageTabsIntroSection(props: { className?: string; children: React.ReactNode }) {
    return <Layout className={clsx('pt-10', props.className)}>{props.children}</Layout>;
}

/**
 * A panel displaying the content of a page's tab
 */
export function PageTabPanel(props: {
    children: React.ReactNode;
    className?: string;
    withAside?: boolean;
}) {
    return (
        <div
            className={clsx(
                'h-full',
                props.withAside && 'flex gap-6 max-lg:flex-col',
                props.className
            )}
        >
            <React.Suspense fallback={<LoadingPanel size="large" />}>
                {props.children}
            </React.Suspense>
        </div>
    );
}

export function PageTabMain(props: { children: React.ReactNode; className?: string }) {
    return <div className={clsx('flex flex-1', props.className)}>{props.children}</div>;
}

export function PageTabAside(props: { children: React.ReactNode }) {
    return (
        <aside className="flex w-full flex-col gap-8 py-2 max-lg:border-t max-lg:pt-6 lg:min-w-[264px] lg:max-w-[320px] lg:border-l lg:pl-6">
            {props.children}
        </aside>
    );
}

export function PageTabAsideSection(props: { title?: string; children: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-2">
            {props.title ? <h3 className="text-ui-heading-group">{props.title}</h3> : null}
            {props.children}
        </div>
    );
}
